/**
 * Environment utilities
 * Reads runtime configuration from window._env_ (injected by FastAPI)
 * Falls back to Vite import.meta.env for local development
 */

const DEV_FASTAPI_DEFAULT = 'http://localhost:8000';

/**
 * Resolve FastAPI origin for API and media proxy calls.
 * Production: same origin as the SPA (no internal URL in config.js).
 * Development: Vite env or localhost when the dev server proxies /api.
 */
export const getFastAPIUrl = (): string => {
  if (typeof window !== 'undefined' && window._env_?.AIPORTAL_FASTAPI_URL) {
    return window._env_.AIPORTAL_FASTAPI_URL.replace(/\/$/, '');
  }

  const viteFastApiUrl = import.meta.env.VITE_AIPORTAL_FASTAPI_URL;
  if (viteFastApiUrl) {
    return String(viteFastApiUrl).replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return DEV_FASTAPI_DEFAULT;
};

/**
 * Get base URL for the application
 */
export const getAppBaseUrl = (): string => {
  if (window._env_?.AIPORTAL_APP_BASE_URL) {
    return window._env_.AIPORTAL_APP_BASE_URL;
  }
  return getFastAPIUrl();
};

/**
 * Get API base URL
 */
export const getApiBaseUrl = (): string => {
  if (window._env_?.AIPORTAL_API_BASE_URL) {
    return window._env_.AIPORTAL_API_BASE_URL;
  }

  const viteApiBase = import.meta.env.VITE_AIPORTAL_API_BASE_URL;
  if (viteApiBase) {
    return String(viteApiBase);
  }

  return `${getFastAPIUrl()}/api`;
};

/**
 * Get redirect URI for OAuth
 */
export const getRedirectUri = (): string => {
  const envRedirectUri =
    window._env_?.AIPORTAL_AZURE_REDIRECT_URI ||
    window._env_?.AIPORTAL_APP_REDIRECT_URI;
  if (envRedirectUri) {
    return envRedirectUri;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/login`;
  }
  return '';
};

/**
 * Get Azure AD authority
 */
export const getAzureAuthority = (): string => {
  return window._env_?.AIPORTAL_AZURE_AUTHORITY || import.meta.env.VITE_AIPORTAL_AZURE_AUTHORITY || '';
};

/**
 * Get Azure AD client ID
 */
export const getAzureClientId = (): string => {
  return window._env_?.AIPORTAL_AZURE_CLIENT_ID || import.meta.env.VITE_AIPORTAL_AZURE_CLIENT_ID || '';
};

/**
 * Get Azure AD scopes
 */
export const getAzureScopes = (): string[] => {
  const scopes =
    window._env_?.AIPORTAL_AZURE_SCOPES ||
    import.meta.env.VITE_AIPORTAL_AZURE_SCOPES ||
    'User.Read';
  return scopes.split(',').map((s: string) => s.trim());
};

/**
 * Get auth provider (azure-ad or local)
 */
export const getAuthProvider = (): string => {
  return (
    window._env_?.AIPORTAL_AUTH_PROVIDER ||
    import.meta.env.VITE_AIPORTAL_AUTH_PROVIDER ||
    'local'
  );
};

/**
 * Get Drupal media URL for static assets (images, files)
 */
export const getDrupalMediaUrl = (): string => {
  return window._env_?.AIPORTAL_DRUPAL_MEDIA_URL || getFastAPIUrl();
};

/**
 * Base URL for Azure blob-backed media via the API proxy (storage URL is server-only).
 */
export const getAzureBlobStorageUrl = (): string => {
  return `${getFastAPIUrl()}/api/v1/media/azure`;
};
